import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import LoadingSpinner from '../../components/LoadingSpinner';

interface Question {
  id: string;
  question: string;
  options: string[];
  weights: Record<string, number>;
}

const QuizManager: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showInlineEdit, setShowInlineEdit] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [formData, setFormData] = useState({
    question: '',
    options: ['', '', '', ''],
    weights: [0, 0, 0, 0]
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuestions();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (editingQuestion) {
      setFormData({
        question: editingQuestion.question,
        options: editingQuestion.options,
        weights: editingQuestion.options.map(opt => editingQuestion.weights[opt] || 0)
      });
    } else {
      setFormData({
        question: '',
        options: ['', '', '', ''],
        weights: [0, 0, 0, 0]
      });
    }
  }, [editingQuestion]);

  const fetchQuestions = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('quiz_questions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Process the questions to ensure proper format
      const processedQuestions = (data || []).map(question => {
        // Ensure weights is properly structured
        const weights = typeof question.weights === 'object' ? question.weights : {};
        
        return {
          ...question,
          weights: weights
        };
      });
      
      setQuestions(processedQuestions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Failed to load questions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setError(null);
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('User not authenticated');

      const weightsObject = formData.options.reduce((acc, opt, idx) => ({
        ...acc,
        [opt]: parseInt(String(formData.weights[idx])) || 0
      }), {});

      const questionData = {
        id: editingQuestion?.id || uuidv4(),
        question: formData.question,
        options: formData.options,
        weights: weightsObject,
        created_by: user.id
      };

      let error;
      if (editingQuestion) {
        ({ error } = await supabase
          .from('quiz_questions')
          .update(questionData)
          .eq('id', editingQuestion.id));
      } else {
        ({ error } = await supabase
          .from('quiz_questions')
          .insert([questionData]));
      }

      if (error) throw error;

      await fetchQuestions();
      setShowModal(false);
      setEditingQuestion(null);
    } catch (error) {
      console.error('Error saving question:', error);
      setError('Failed to save question. Please make sure you are logged in and have the necessary permissions.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this question?')) return;

    try {
      setError(null);
      const { error } = await supabase
        .from('quiz_questions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      setError('Failed to delete question. Please make sure you have the necessary permissions.');
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const handleWeightChange = (index: number, value: string) => {
    const newWeights = [...formData.weights];
    newWeights[index] = parseInt(value) || 0;
    setFormData(prev => ({ ...prev, weights: newWeights }));
  };

  if (authLoading || isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center">
            <p className="text-red-600">Please log in to access this page.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Quiz Manager</h1>
            <button
              onClick={() => {
                setEditingQuestion(null);
                setShowInlineEdit(true);
              }}
              className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Question
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {/* Inline question editor */}
          {showInlineEdit && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">
                {editingQuestion ? 'Edit Question' : 'Add New Question'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question
                  </label>
                  <input
                    type="text"
                    value={formData.question}
                    onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                    className="w-full p-2 border rounded-md"
                    placeholder="Enter your question"
                  />
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Options and Weights
                  </label>
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex gap-4">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        className="flex-1 p-2 border rounded-md"
                        placeholder={`Option ${index + 1}`}
                      />
                      <input
                        type="number"
                        value={String(formData.weights[index])}
                        onChange={(e) => handleWeightChange(index, e.target.value)}
                        className="w-24 p-2 border rounded-md"
                        placeholder="Weight"
                        min="0"
                        max="100"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowInlineEdit(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleSave();
                    setShowInlineEdit(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {questions.map((question) => (
                <li key={question.id} className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {question.question}
                      </h3>
                      <ul className="space-y-2">
                        {question.options.map((option, index) => (
                          <li key={index} className="flex items-center text-gray-600">
                            <span className="w-8">{index + 1}.</span>
                            <span>{option}</span>
                            <span className="ml-4 text-sm text-gray-500">
                              (Weight: {question.weights[option]})
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setEditingQuestion(question);
                          setShowInlineEdit(true);
                        }}
                        className="p-2 text-gray-600 hover:text-blue-600"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(question.id)}
                        className="p-2 text-gray-600 hover:text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4">
            <h2 className="text-xl font-bold mb-4">
              {editingQuestion ? 'Edit Question' : 'Add New Question'}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question
                </label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                  className="w-full p-2 border rounded-md"
                  placeholder="Enter your question"
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Options and Weights
                </label>
                {formData.options.map((option, index) => (
                  <div key={index} className="flex gap-4">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      className="flex-1 p-2 border rounded-md"
                      placeholder={`Option ${index + 1}`}
                    />
                    <input
                      type="number"
                      value={String(formData.weights[index])}
                      onChange={(e) => handleWeightChange(index, e.target.value)}
                      className="w-24 p-2 border rounded-md"
                      placeholder="Weight"
                      min="0"
                      max="100"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default QuizManager;
